/**
 * Which card the portal shows beside the player on screen (#494, ADR 0010 Decision 5 amended 2026-10-28).
 *
 * <b>Members read names, not cards</b>, and `GET /api/players/{id}` carries no card for any caller. A card ID is read
 * by the room's staff and by the person holding it — so the portal shows one only where the player on screen is the
 * caller's own, and takes it from the caller's membership (`/api/me`'s `memberships[].cardId`).
 *
 * <b>The comparison is on the player, not on whether a membership exists.</b> A member of staff who holds a card at
 * the room has a membership there that is entirely real — and it names <i>their</i> player. Browsing somebody else's
 * portal, showing that card would put the caller's own card beside another player's name. `usePlayer` already nulls
 * `membership` while staff browse; this does not rely on it.
 *
 * Pure and import-free, so `cards.test.js` runs it under `node --test`.
 */

/** The minimum of the player on screen this rule reads. */
export interface ViewedPlayer {
  readonly id: string;
}

/** The minimum of a `Membership` this rule reads. */
export interface HeldCard {
  readonly playerId: string;
  readonly cardId: string;
}

/** The caller's own card when the player on screen is theirs, and null otherwise — never another player's. */
export function ownCardFor(player: ViewedPlayer | null, membership: HeldCard | null): string | null {
  if (player === null || membership === null) return null;
  return membership.playerId === player.id ? membership.cardId : null;
}
